// De vensterbreedte in pixels, als één gedeelde bron voor de hele app.
//
// WAAROM EEN resize-LISTENER EN NIET matchMedia (zoals useIsPhone)
// ---------------------------------------------------------------
// useIsPhone beantwoordt één ja/nee-vraag en kan dus met één media query toe. Hier hebben
// drie schermen elk hun eigen drempel (1400 voor de drawer, 1180 en 1500 voor de
// Assets-kolommen) en de tabellen rekenen bovendien met de breedte zelf. Eén listener die
// één getal levert is dan eenvoudiger dan een media query per drempel — en het is precies
// wat de werkafspraak wil: geen resize-listener per scherm.
//
// Wel rAF-gethrottled: resize vuurt tijdens het slepen op elke frame en dit getal hangt in
// een context boven de hele boom.

import { useEffect, useState } from "react";

// Zonder DOM (SSR, een test die de breedte niet regelt) gaan we uit van een breed venster:
// de brede weergave is de volledige weergave, en dat is de veiligste aanname.
export const FALLBACK_VIEWPORT_WIDTH = 1440;

function readWidth() {
  if (typeof window === "undefined") return FALLBACK_VIEWPORT_WIDTH;
  return window.innerWidth || FALLBACK_VIEWPORT_WIDTH;
}

export default function useViewportWidth() {
  const [vw, setVw] = useState(readWidth);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    // `pending` en niet de frame-id als vlag: vuurt requestAnimationFrame synchroon
    // (jsdom, een gestubde rAF in tests), dan zet `frame = raf(apply)` de id ná afloop
    // van apply terug — en blijft de vlag voor altijd hangen, waarna elke volgende
    // resize genegeerd wordt.
    let pending = false;
    let frame = 0;
    const apply = () => {
      pending = false;
      setVw(window.innerWidth || FALLBACK_VIEWPORT_WIDTH);
    };
    const onResize = () => {
      if (pending) return;
      pending = true;
      frame = window.requestAnimationFrame
        ? window.requestAnimationFrame(apply)
        : window.setTimeout(apply, 16);
    };

    window.addEventListener("resize", onResize);
    // Eén keer synchroniseren: tussen de eerste render en dit effect kan het venster al
    // van breedte veranderd zijn (devtools die opengaat, een herstelde vensterpositie).
    apply();

    return () => {
      window.removeEventListener("resize", onResize);
      if (pending && frame && window.cancelAnimationFrame) window.cancelAnimationFrame(frame);
    };
  }, []);

  return vw;
}
